import type { components } from "@/api-schema";
import type { RelocationOrderStatus } from "@/api-schema-enums";
import {
  CompanyType,
  PathsV1BranchSearchPostParametersQueryExtend,
  PathsV1BranchSearchPostParametersQueryOrderByDirection,
  PathsV1BranchSearchPostParametersQueryOrderByField,
  PathsV1BranchUuidGetParametersQueryExtend,
  PathsV1CompanySearchPostParametersQueryOrderByDirection,
  PathsV1CompanySearchPostParametersQueryOrderByField,
  PathsV1UserSearchPostParametersQueryOrderByDirection,
  PathsV1UserSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { useBranchByUuidQuery } from "@/common/api/branch/use-branch-by-uuid.query";
import { useBranchSearchQuery } from "@/common/api/branch/use-branch-search.query";
import { useCompanyByUuidQuery } from "@/common/api/company/use-company-by-uuid.query";
import { useCompanySearchQuery } from "@/common/api/company/use-company-search.query";
import { useUserByUuidQuery } from "@/common/api/user/use-user-by-uuid.query";
import { useUserSearchQuery } from "@/common/api/user/use-user-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { ComboboxPaginationState } from "@/components/ui/combobox";
import { useMemo } from "react";
import {
  MappedBranch,
  MappedTransportCompany,
  MappedUser,
} from "@/modules/fit/internal/relocation-order/admin/_components/relocation-order-admin-form-fields";
import {
  ALLOW_DESTINATION_LOCATION_STATUSES,
  REQUIRES_DRIVER_STATUSES,
} from "@/modules/fit/internal/relocation-order/admin/_constants/relocation-order-statuses";

type Branch = components["schemas"]["Branch"];
type Company = components["schemas"]["Company"];
type User = components["schemas"]["User"];

interface UseRelocationOrderFormOptionsProps {
  selectedTransportCompanyUuid?: string;
  selectedVehicleOriginUuid?: string;
  selectedOriginBranchUuid?: string;
  selectedCurrentBranchUuid?: string;
  selectedStatus?: RelocationOrderStatus;
  selectedDestinationBranchUuid?: string;
  selectedPickedUpByUuid?: string;
  selectedOrderedByUuid?: string;
}

type UseRelocationOrderFormOptionsResult = {
  selectedOriginBranch?: Branch;
  selectedCurrentBranch?: Branch;
  selectedDestinationBranch?: Branch;
  mappedBranches: MappedBranch[];
  mappedDestinationBranches: MappedBranch[];
  mappedTransportCompanies: MappedTransportCompany[];
  mappedTransportCompanyUsers: MappedUser[];
  mappedOrderedByUsers: MappedUser[];
  branchComboboxState: ComboboxPaginationState;
  companyComboboxState: ComboboxPaginationState;
  driverComboboxState: ComboboxPaginationState;
  orderedByComboboxState: ComboboxPaginationState;
  isLoading: boolean;
};

function mapBranches(branches: Branch[]): MappedBranch[] {
  const mapped: MappedBranch[] = [];

  for (let i = 0; i < branches.length; i += 1) {
    const branch = branches[i];
    const label = branch.name;

    mapped.push({
      value: branch.uuid,
      label,
      searchText: label.toLowerCase(),
    });
  }

  return mapped;
}

function mapTransportCompanies(companies: Company[]): MappedTransportCompany[] {
  const mapped: MappedTransportCompany[] = [];

  for (let i = 0; i < companies.length; i += 1) {
    const company = companies[i];
    const searchText = `${company.name} ${company.technicalId}`.toLowerCase();

    mapped.push({
      value: company.uuid,
      label: company.name,
      labelShort: company.technicalId,
      searchText,
    });
  }

  return mapped;
}

function mapTransportCompanyUsers(users: User[]): MappedUser[] {
  const mapped: MappedUser[] = [];

  for (let i = 0; i < users.length; i += 1) {
    const user = users[i];

    mapped.push({
      value: user.uuid,
      label: `${user.lastName} ${user.firstName}`,
    });
  }

  return mapped;
}

export function useRelocationOrderFormOptions({
  selectedTransportCompanyUuid,
  selectedVehicleOriginUuid,
  selectedOriginBranchUuid,
  selectedCurrentBranchUuid,
  selectedStatus,
  selectedDestinationBranchUuid,
  selectedPickedUpByUuid,
  selectedOrderedByUuid,
}: UseRelocationOrderFormOptionsProps = {}): UseRelocationOrderFormOptionsResult {
  const hasSelectedOriginBranchUuid = !!selectedOriginBranchUuid;
  const hasSelectedCurrentBranchUuid = !!selectedCurrentBranchUuid;
  const hasSelectedDestinationBranchUuid = !!selectedDestinationBranchUuid;
  const hasSelectedTransportCompanyUuid = !!selectedTransportCompanyUuid;
  const hasSelectedPickedUpByUuid = !!selectedPickedUpByUuid;
  const hasSelectedOrderedByUuid = !!selectedOrderedByUuid;

  const {
    pageSize: branchPageSize,
    debouncedSearchTerm: debouncedBranchSearchTerm,
    createPaginationState: createBranchPaginationState,
  } = useComboboxPagination();

  const {
    pageSize: companyPageSize,
    debouncedSearchTerm: debouncedCompanySearchTerm,
    createPaginationState: createCompanyPaginationState,
  } = useComboboxPagination();

  const {
    pageSize: driverPageSize,
    debouncedSearchTerm: debouncedDriverSearchTerm,
    createPaginationState: createDriverPaginationState,
  } = useComboboxPagination();

  const {
    pageSize: orderedByPageSize,
    debouncedSearchTerm: debouncedOrderedBySearchTerm,
    createPaginationState: createOrderedByPaginationState,
  } = useComboboxPagination();

  const { data: branchesData, isPending: isBranchesLoading } = useBranchSearchQuery({
    options: {
      parameters: {
        pageSize: branchPageSize,
        search: debouncedBranchSearchTerm || undefined,
        orderBy: [
          {
            field: PathsV1BranchSearchPostParametersQueryOrderByField.Company,
            direction: PathsV1BranchSearchPostParametersQueryOrderByDirection.Asc,
          },
          {
            field: PathsV1BranchSearchPostParametersQueryOrderByField.Address,
            direction: PathsV1BranchSearchPostParametersQueryOrderByDirection.Asc,
          },
        ],
        extend: [
          PathsV1BranchSearchPostParametersQueryExtend.Company,
          PathsV1BranchSearchPostParametersQueryExtend.Brands,
          PathsV1BranchSearchPostParametersQueryExtend.Address,
          PathsV1BranchSearchPostParametersQueryExtend.BranchIdentifiers,
        ],
      },
    },
  });

  const { data: selectedOriginBranchData, isPending: isSelectedOriginBranchLoading } = useBranchByUuidQuery(
    selectedOriginBranchUuid || "",
    {
      parameters: {
        extend: [
          PathsV1BranchUuidGetParametersQueryExtend.Company,
          PathsV1BranchUuidGetParametersQueryExtend.Brands,
          PathsV1BranchUuidGetParametersQueryExtend.Address,
          PathsV1BranchUuidGetParametersQueryExtend.BranchIdentifiers,
        ],
      },
    },
  );

  const { data: selectedDestinationBranchData, isPending: isSelectedDestinationBranchLoading } = useBranchByUuidQuery(
    selectedDestinationBranchUuid || "",
    {
      parameters: {
        extend: [
          PathsV1BranchUuidGetParametersQueryExtend.Company,
          PathsV1BranchUuidGetParametersQueryExtend.Brands,
          PathsV1BranchUuidGetParametersQueryExtend.Address,
          PathsV1BranchUuidGetParametersQueryExtend.BranchIdentifiers,
        ],
      },
    },
  );

  const { data: selectedCurrentBranchData, isPending: isSelectedCurrentBranchLoading } = useBranchByUuidQuery(
    selectedCurrentBranchUuid || "",
    {
      parameters: {
        extend: [
          PathsV1BranchUuidGetParametersQueryExtend.Company,
          PathsV1BranchUuidGetParametersQueryExtend.Brands,
          PathsV1BranchUuidGetParametersQueryExtend.Address,
          PathsV1BranchUuidGetParametersQueryExtend.BranchIdentifiers,
        ],
      },
    },
  );

  const mappedBranchesFromApi: MappedBranch[] = useMemo(() => {
    return mapBranches(branchesData?.data ?? []);
  }, [branchesData?.data]);

  const mappedHydratedBranches: MappedBranch[] = useMemo(() => {
    const selectedBranches: Branch[] = [];

    if (selectedOriginBranchData?.uuid) {
      selectedBranches.push(selectedOriginBranchData);
    }

    if (selectedCurrentBranchData?.uuid) {
      selectedBranches.push(selectedCurrentBranchData);
    }

    if (selectedDestinationBranchData?.uuid) {
      selectedBranches.push(selectedDestinationBranchData);
    }

    return mapBranches(selectedBranches);
  }, [selectedCurrentBranchData, selectedDestinationBranchData, selectedOriginBranchData]);

  const mappedBranches: MappedBranch[] = useMemo(() => {
    return mergeByValue(mappedBranchesFromApi, mappedHydratedBranches);
  }, [mappedBranchesFromApi, mappedHydratedBranches]);

  const mappedDestinationBranches: MappedBranch[] = useMemo(() => {
    const effectiveOriginBranchUuid = selectedOriginBranchUuid || selectedVehicleOriginUuid;

    if (!effectiveOriginBranchUuid) {
      return mappedBranches;
    }

    if (selectedStatus && ALLOW_DESTINATION_LOCATION_STATUSES.includes(selectedStatus)) {
      return mappedBranches;
    }

    return mappedBranches.filter((branch) => {
      if (branch.value !== effectiveOriginBranchUuid) {
        return true;
      }

      return branch.value === selectedDestinationBranchUuid;
    });
  }, [
    mappedBranches,
    selectedDestinationBranchUuid,
    selectedOriginBranchUuid,
    selectedStatus,
    selectedVehicleOriginUuid,
  ]);

  const { data: companiesData, isPending: isCompaniesLoading } = useCompanySearchQuery({
    options: {
      parameters: {
        pageSize: companyPageSize,
        search: debouncedCompanySearchTerm || undefined,
        orderBy: [
          {
            field: PathsV1CompanySearchPostParametersQueryOrderByField.Name,
            direction: PathsV1CompanySearchPostParametersQueryOrderByDirection.Asc,
          },
        ],
      },
      body: {
        type: [CompanyType.Transport],
      },
    },
  });

  const { data: selectedTransportCompanyData, isPending: isSelectedTransportCompanyLoading } = useCompanyByUuidQuery(
    selectedTransportCompanyUuid || "",
  );

  const mappedTransportCompaniesFromApi: MappedTransportCompany[] = useMemo(() => {
    return mapTransportCompanies(companiesData?.data ?? []);
  }, [companiesData?.data]);

  const mappedHydratedTransportCompanies: MappedTransportCompany[] = useMemo(() => {
    if (!selectedTransportCompanyData?.uuid) {
      return [];
    }

    return mapTransportCompanies([selectedTransportCompanyData]);
  }, [selectedTransportCompanyData]);

  const mappedTransportCompanies: MappedTransportCompany[] = useMemo(() => {
    return mergeByValue(mappedTransportCompaniesFromApi, mappedHydratedTransportCompanies);
  }, [mappedTransportCompaniesFromApi, mappedHydratedTransportCompanies]);

  const shouldLoadTransportCompanyUsers =
    !!selectedTransportCompanyUuid && !!selectedStatus && REQUIRES_DRIVER_STATUSES.includes(selectedStatus);

  const { data: usersData, isPending: isUsersLoading } = useUserSearchQuery({
    options: {
      parameters: {
        pageSize: driverPageSize,
        search: debouncedDriverSearchTerm || undefined,
        orderBy: [
          {
            field: PathsV1UserSearchPostParametersQueryOrderByField.LastName,
            direction: PathsV1UserSearchPostParametersQueryOrderByDirection.Asc,
          },
          {
            field: PathsV1UserSearchPostParametersQueryOrderByField.FirstName,
            direction: PathsV1UserSearchPostParametersQueryOrderByDirection.Asc,
          },
        ],
      },
      body: {
        companies: selectedTransportCompanyUuid ? [selectedTransportCompanyUuid] : [],
      },
    },
    enabled: shouldLoadTransportCompanyUsers,
  });

  const { data: selectedPickedUpByData, isPending: isSelectedPickedUpByLoading } = useUserByUuidQuery(
    selectedPickedUpByUuid || "",
  );

  const mappedTransportCompanyUsersFromApi: MappedUser[] = useMemo(() => {
    return mapTransportCompanyUsers(usersData?.data ?? []);
  }, [usersData?.data]);

  const mappedHydratedTransportCompanyUsers: MappedUser[] = useMemo(() => {
    if (!selectedPickedUpByData?.uuid) {
      return [];
    }

    return mapTransportCompanyUsers([selectedPickedUpByData]);
  }, [selectedPickedUpByData]);

  const mappedTransportCompanyUsers: MappedUser[] = useMemo(() => {
    return mergeByValue(mappedTransportCompanyUsersFromApi, mappedHydratedTransportCompanyUsers);
  }, [mappedTransportCompanyUsersFromApi, mappedHydratedTransportCompanyUsers]);

  const { data: orderedByUsersData, isPending: isOrderedByUsersLoading } = useUserSearchQuery({
    options: {
      parameters: {
        pageSize: orderedByPageSize,
        search: debouncedOrderedBySearchTerm || undefined,
        orderBy: [
          {
            field: PathsV1UserSearchPostParametersQueryOrderByField.LastName,
            direction: PathsV1UserSearchPostParametersQueryOrderByDirection.Asc,
          },
          {
            field: PathsV1UserSearchPostParametersQueryOrderByField.FirstName,
            direction: PathsV1UserSearchPostParametersQueryOrderByDirection.Asc,
          },
        ],
      },
      body: {},
    },
  });

  const { data: selectedOrderedByData, isPending: isSelectedOrderedByLoading } = useUserByUuidQuery(
    selectedOrderedByUuid || "",
  );

  const mappedOrderedByUsersFromApi: MappedUser[] = useMemo(() => {
    return mapTransportCompanyUsers(orderedByUsersData?.data ?? []);
  }, [orderedByUsersData?.data]);

  const mappedHydratedOrderedByUsers: MappedUser[] = useMemo(() => {
    if (!selectedOrderedByData?.uuid) {
      return [];
    }

    return mapTransportCompanyUsers([selectedOrderedByData]);
  }, [selectedOrderedByData]);

  const mappedOrderedByUsers: MappedUser[] = useMemo(() => {
    return mergeByValue(mappedOrderedByUsersFromApi, mappedHydratedOrderedByUsers);
  }, [mappedOrderedByUsersFromApi, mappedHydratedOrderedByUsers]);

  const loadedBranchCount = branchesData?.data?.length ?? 0;

  const branchComboboxState: ComboboxPaginationState = createBranchPaginationState(
    branchesData?.meta.total,
    loadedBranchCount,
    isBranchesLoading,
  );

  const companyComboboxState: ComboboxPaginationState = createCompanyPaginationState(
    companiesData?.meta.total,
    mappedTransportCompanies.length,
    isCompaniesLoading,
  );

  const driverComboboxState: ComboboxPaginationState = createDriverPaginationState(
    usersData?.meta.total,
    mappedTransportCompanyUsers.length,
    isUsersLoading,
  );

  const orderedByComboboxState: ComboboxPaginationState = createOrderedByPaginationState(
    orderedByUsersData?.meta.total,
    mappedOrderedByUsers.length,
    isOrderedByUsersLoading,
  );

  const isLoading =
    isBranchesLoading ||
    (hasSelectedOriginBranchUuid && isSelectedOriginBranchLoading) ||
    (hasSelectedCurrentBranchUuid && isSelectedCurrentBranchLoading) ||
    (hasSelectedDestinationBranchUuid && isSelectedDestinationBranchLoading) ||
    isCompaniesLoading ||
    (hasSelectedTransportCompanyUuid && isSelectedTransportCompanyLoading) ||
    (shouldLoadTransportCompanyUsers && isUsersLoading) ||
    (hasSelectedPickedUpByUuid && isSelectedPickedUpByLoading) ||
    isOrderedByUsersLoading ||
    (hasSelectedOrderedByUuid && isSelectedOrderedByLoading);

  return {
    selectedOriginBranch: selectedOriginBranchData,
    selectedCurrentBranch: selectedCurrentBranchData,
    selectedDestinationBranch: selectedDestinationBranchData,
    mappedBranches,
    mappedDestinationBranches,
    mappedTransportCompanies,
    mappedTransportCompanyUsers,
    mappedOrderedByUsers,
    branchComboboxState,
    companyComboboxState,
    driverComboboxState,
    orderedByComboboxState,
    isLoading,
  };
}

function mergeByValue<T extends { value: string }>(primaryOptions: T[], secondaryOptions: T[]): T[] {
  const mergedByValue: Record<string, T> = {};

  for (let i = 0; i < primaryOptions.length; i += 1) {
    const option = primaryOptions[i];
    mergedByValue[option.value] = option;
  }

  for (let i = 0; i < secondaryOptions.length; i += 1) {
    const option = secondaryOptions[i];

    if (!mergedByValue[option.value]) {
      mergedByValue[option.value] = option;
    }
  }

  return Object.values(mergedByValue);
}
